import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, DateTimePicker } from '@/components';
import { useModalClose, useBodyScrollLock } from '@/common/hooks';

export function RescheduleModal({
  isOpen,
  onClose,
  onReschedule,
  isRescheduling,
  title = 'Reschedule Follow-up',
  description = 'Select a new date and time to run this follow-up.',
  serializeDateTime = (date) => date.toISOString(),
}) {
  const [newDate, setNewDate] = useState(null);
  const modalRef = useModalClose({
    isOpen,
    onClose: () => {
      if (!isRescheduling) onClose();
    },
  });

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) setNewDate(null);
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleReschedule = () => {
    if (!newDate) return;
    onReschedule(serializeDateTime(newDate));
  };

  const handleClose = () => {
    if (!isRescheduling) onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        ref={modalRef}
        data-modal-root
        role="dialog"
        aria-modal="true"
        className="w-full max-w-sm bg-white border border-[#EBF4EA] rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
      >
        <div className="p-5 border-b border-[#EBF4EA]">
          <h3 className="text-lg font-bold text-[#1F2241]">{title}</h3>
          <p className="text-sm text-[#868686] mt-1">{description}</p>
        </div>

        <div className="p-5 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              New Date & Time <span className="text-red-500">*</span>
            </label>
            <DateTimePicker
              selected={newDate}
              onChange={(date) => setNewDate(date)}
              mode="datetime"
              allowPastDates={false}
              disablePastTime={true}
              placeholderText="Select new date & time"
              disabled={isRescheduling}
              className="w-full h-11 px-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors"
            />
          </div>
        </div>

        <div className="p-5 border-t border-[#EBF4EA] flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isRescheduling}
            className="h-9 px-4 text-sm font-medium border border-[#EBF4EA] hover:bg-[#F5F5F5] hover:text-[#1F2241]"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleReschedule}
            disabled={isRescheduling || !newDate}
            className="h-9 px-6 text-sm font-semibold"
          >
            {isRescheduling ? 'Rescheduling...' : 'Reschedule'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
